/* eslint-disable max-lines */
'use client';

import { Slot } from '@radix-ui/react-slot';
import { ChevronRightIcon, MenuIcon, PanelLeftIcon } from 'lucide-react';
import * as React from 'react';

import { cn, useIsMobile } from '@heidi/utils';
import { Button } from '../../../button';
import { useDesignSystemIntl } from '../../../hooks/use-design-system-intl';
import { Input } from '../../../input';
import {
  NavigationMenuList,
  NavigationMenuVerticalTracked,
  SidebarPanelProvider,
  SidebarPanelViewport,
} from '../../../navigation-menu';
import { Separator } from '../../../separator';
import { Skeleton } from '../../../skeleton';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../tooltip';
import { useSidebarState } from '../../hooks/use-page-shell-state';
import { PanelFloating, usePanelContext } from '../panel';
import {
  sidebarActionVariants,
  sidebarBaseClasses,
  sidebarContainerVariants,
  sidebarContentVariants,
  sidebarDesktopWrapperClasses,
  sidebarFooterVariants,
  sidebarGapVariants,
  sidebarGroupActionVariants,
  sidebarGroupClasses,
  sidebarGroupContentClasses,
  sidebarGroupLabelVariants,
  sidebarHeaderVariants,
  sidebarInnerVariants,
  sidebarInputClasses,
  sidebarInsetClasses,
  sidebarMenuActionShowOnHoverClasses,
  sidebarMenuActionVariants,
  sidebarMenuBadgeVariants,
  sidebarMenuButtonLabelIconsVariants,
  sidebarMenuButtonVariants,
  sidebarMenuItemClasses,
  sidebarMenuSkeletonClasses,
  sidebarMenuSubButtonVariants,
  sidebarMenuSubItemClasses,
  sidebarMenuSubVariants,
  sidebarMenuVariants,
  sidebarProviderClasses,
  sidebarRailVariants,
  sidebarSeparatorClasses,
} from './sidebar.styles';
import type {
  SidebarActionProps,
  SidebarCollapsible,
  SidebarContentProps,
  SidebarContextProps,
  SidebarFooterProps,
  SidebarGroupActionProps,
  SidebarGroupContentProps,
  SidebarGroupLabelProps,
  SidebarGroupProps,
  SidebarHeaderProps,
  SidebarInputProps,
  SidebarInsetProps,
  SidebarMenuActionProps,
  SidebarMenuBadgeProps,
  SidebarMenuButtonProps,
  SidebarMenuButtonVariant,
  SidebarMenuItemProps,
  SidebarMenuProps,
  SidebarMenuSkeletonProps,
  SidebarMenuSubButtonProps,
  SidebarMenuSubItemProps,
  SidebarMenuSubProps,
  SidebarProps,
  SidebarProviderProps,
  SidebarRailProps,
  SidebarSeparatorProps,
  SidebarTriggerProps,
} from './sidebar.types';
import {
  SIDEBAR_HEADER_HEIGHT,
  SIDEBAR_HEADER_ICON_SIZE,
  SIDEBAR_KEYBOARD_SHORTCUT,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_ICON,
  SIDEBAR_WIDTH_LABEL_ICONS,
} from './sidebar.types';

// ============================================================================
// Configuration
// ============================================================================

/**
 * Feature flag for sidebar panel functionality (floating panels, mobile panel views).
 *
 * When disabled (default):
 * - Mobile sidebar renders children directly without PanelMobile wrapper
 * - SidebarMenuButton ignores panelContent/panelAction/panelId props
 * - Floating panels and mobile panel navigation are not rendered
 *
 * Set to `true` to enable panel features when ready for production.
 */
const IS_SIDEBAR_PANELS_ENABLED = false;

// ============================================================================
// Context
// ============================================================================

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider.');
  }
  return context;
}

/**
 * Helper hook to get the effective collapsible state for CVA variants.
 * Returns 'none' when expanded, otherwise returns the actual collapsible mode.
 *
 * Use this when passing collapsible state to CVA variant functions.
 *
 * @example
 * ```tsx
 * const collapsible = useCollapsibleVariant();
 * return <div className={cn(sidebarFooterVariants({ collapsible }))} />;
 * ```
 */
export function useCollapsibleVariant(): SidebarCollapsible {
  const { state, collapsible } = useSidebar();
  const result = state === 'collapsed' ? collapsible : 'none';

  return result;
}

// ============================================================================
// SidebarProvider
// ============================================================================

const SidebarProvider = React.forwardRef<HTMLDivElement, SidebarProviderProps>(
  (
    {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for API compatibility, localStorage persistence takes precedence
      defaultOpen = false,
      open: openProp,
      onOpenChange: setOpenProp,
      forceMobile = false,
      defaultCollapsible = 'icon',
      className,
      style,
      children,
      ...props
    },
    ref
  ) => {
    // Use 640px (SM) breakpoint to match the product standard
    // forceMobile overrides for MobileSidebar usage
    const isMobileBreakpoint = useIsMobile();
    const isMobile = forceMobile || isMobileBreakpoint;
    // Initialize with defaultCollapsible to prevent flicker on mount
    // (the Sidebar child will sync its collapsible prop via useLayoutEffect, but this ensures first render is correct)
    const [collapsible, setCollapsible] =
      React.useState<SidebarContextProps['collapsible']>(defaultCollapsible);
    const [variant, setVariant] =
      React.useState<SidebarContextProps['variant']>('sidebar');

    // Use localStorage-persisted state for internal state management
    const { isExpanded: persistedExpanded, setIsExpanded: setPersistedExpanded } =
      useSidebarState();

    // Support controlled mode (openProp) or localStorage-persisted mode
    // Note: defaultOpen is ignored when using localStorage persistence (initial value comes from storage)
    // In label-icons mode, force open to false (sidebar cannot be expanded)
    const open = collapsible === 'label-icons' ? false : (openProp ?? persistedExpanded);
    const setOpen = React.useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const openState = typeof value === 'function' ? value(open) : value;
        if (setOpenProp) {
          setOpenProp(openState);
        } else {
          setPersistedExpanded(openState);
        }
      },
      [setOpenProp, open, setPersistedExpanded]
    );

    const toggleSidebar = React.useCallback(() => {
      setOpen((open) => !open);
    }, [setOpen]);

    // On mobile, the drawer is always "expanded" when open - desktop collapsed state shouldn't affect it
    const state = isMobile ? 'expanded' : open ? 'expanded' : 'collapsed';

    const contextValue = React.useMemo<SidebarContextProps>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        toggleSidebar,
        collapsible,
        setCollapsible,
        variant,
        setVariant,
      }),
      [
        state,
        open,
        setOpen,
        isMobile,
        toggleSidebar,
        collapsible,
        setCollapsible,
        variant,
        setVariant,
      ]
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            ref={ref}
            data-slot="sidebar-wrapper"
            style={
              {
                '--sidebar-width': SIDEBAR_WIDTH,
                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
                '--sidebar-width-label-icons': SIDEBAR_WIDTH_LABEL_ICONS,
                '--sidebar-header-height': SIDEBAR_HEADER_HEIGHT,
                '--sidebar-header-icon-size': SIDEBAR_HEADER_ICON_SIZE,
                ...style,
              } as React.CSSProperties
            }
            className={cn(sidebarProviderClasses, className)}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarContext.Provider>
    );
  }
);
SidebarProvider.displayName = 'SidebarProvider';

// ============================================================================
// Sidebar
// ============================================================================

const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  (
    {
      variant = 'sidebar',
      collapsible = 'icon',
      withRail = true,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const { isMobile, state, setCollapsible, setVariant, toggleSidebar } = useSidebar();

    // Sync collapsible and variant props to context BEFORE paint to prevent transition flicker
    // Using useLayoutEffect ensures values are set before the browser paints,
    // avoiding the flash where components render with defaults then transition
    React.useLayoutEffect(() => {
      setCollapsible(collapsible);
    }, [collapsible, setCollapsible]);

    React.useLayoutEffect(() => {
      setVariant(variant);
    }, [variant, setVariant]);

    // Get the effective collapsible state for CVA variants
    const collapsibleVariant = state === 'collapsed' ? collapsible : 'none';

    /**
     * Handles clicks on the collapsed sidebar background to expand it.
     *
     * This is a mouse-only convenience feature that allows users to click anywhere
     * on the collapsed sidebar (not just the rail) to expand it. The cursor changes
     * to `cursor-e-resize` when collapsed to indicate this affordance.
     *
     * Keyboard users can use SidebarRail (focusable) or the 'H' keyboard shortcut.
     *
     * Guards:
     * 1. Only triggers when sidebar is collapsed (expanded state ignores clicks)
     * 2. Ignores clicks on interactive elements (buttons, links, inputs, etc.)
     * 3. Ignores clicks when a dropdown/popover is open (prevents unintended expansion
     *    when clicking dropdown items that close the menu and bubble the click event)
     *
     * Note: This handler is only attached when collapsible !== 'label-icons'.
     * In label-icons mode, the sidebar is always collapsed and cannot be expanded.
     */
    const handleBackgroundClick = React.useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        // Guard 1: Only expand when collapsed
        if (state !== 'collapsed') return;

        // Guard 2: Don't toggle if click originated from an interactive element
        const target = event.target as HTMLElement;
        const isInteractive = target.closest(
          'button, a, input, textarea, select, label, [data-sidebar="rail"], [role="button"], [role="link"]'
        );
        if (isInteractive) return;

        // Guard 3: Don't toggle if a dropdown/popover trigger inside the sidebar is open
        // This prevents expansion when clicking dropdown items that close the menu
        // and bubble the click event up to the sidebar background
        const sidebarElement = event.currentTarget;
        const hasOpenTrigger = sidebarElement.querySelector('[data-state="open"]');
        if (hasOpenTrigger) return;

        toggleSidebar();
      },
      [state, toggleSidebar]
    );

    if (collapsible === 'none') {
      return (
        <div
          ref={ref}
          data-slot="sidebar"
          className={cn(sidebarBaseClasses, className)}
          {...props}
        >
          {children}
        </div>
      );
    }

    // On mobile, Sidebar renders nothing - use MobileSidebar instead
    if (isMobile) {
      return null;
    }

    return (
      <div
        ref={ref}
        data-state={state}
        data-collapsible={state === 'collapsed' ? collapsible : ''}
        data-variant={variant}
        data-slot="sidebar"
        className={sidebarDesktopWrapperClasses}
      >
        <div
          data-slot="sidebar-gap"
          className={sidebarGapVariants({
            collapsible: collapsibleVariant,
            variant,
          })}
        />
        <div
          data-slot="sidebar-container"
          className={cn(
            sidebarContainerVariants({
              collapsible: collapsibleVariant,
              variant,
            }),
            className
          )}
          {...props}
        >
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- pointer-only enhancement: clicking the collapsed sidebar background expands it; keyboard users expand via the SidebarRail / trigger button */}
          <div
            data-sidebar="sidebar"
            data-slot="sidebar-inner"
            className={cn(
              sidebarInnerVariants({ variant }),
              // Show expand cursor when collapsed and expandable (not in label-icons mode)
              state === 'collapsed' &&
                collapsible !== 'label-icons' &&
                'cursor-e-resize rtl:cursor-w-resize'
            )}
            // Only attach click handler when sidebar can be expanded (not in label-icons mode)
            onClick={collapsible !== 'label-icons' ? handleBackgroundClick : undefined}
          >
            {children}
          </div>
        </div>
        {/* Rail rendered outside container so it's not clipped by overflow-hidden */}
        {withRail && <SidebarRail />}
      </div>
    );
  }
);
Sidebar.displayName = 'Sidebar';

// ============================================================================
// SidebarTrigger
// ============================================================================

/**
 * Button to toggle the sidebar open/closed state.
 *
 * Shows different icons based on viewport:
 * - Mobile: MenuIcon (hamburger) — opens the sidebar drawer
 * - Desktop: PanelLeftIcon — collapses/expands the sidebar
 *
 * Note: In PageShell.Header, only the mobile path is used. On desktop,
 * PageShell.Header renders PanelTrigger instead. The desktop icon exists
 * for standalone usage outside of PageShell.Header if needed.
 */
const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>(
  ({ className, onClick, ...props }, ref) => {
    const { toggleSidebar, isMobile } = useSidebar();
    const intl = useDesignSystemIntl();
    const toggleSidebarLabel = intl.formatMessage({
      defaultMessage: 'Toggle Sidebar',
      id: 'r+l8IsJXtT',
      description: 'Accessible label for the page shell sidebar toggle button',
    });

    return (
      <Button
        ref={ref}
        data-sidebar="trigger"
        data-slot="sidebar-trigger"
        variant="ghost"
        size="sm"
        iconOnly
        className={cn('size-11 md:size-7', className)}
        onClick={(event) => {
          onClick?.(event);
          toggleSidebar();
        }}
        {...props}
      >
        {isMobile ? <MenuIcon /> : <PanelLeftIcon />}
        <span className="sr-only">{toggleSidebarLabel}</span>
      </Button>
    );
  }
);
SidebarTrigger.displayName = 'SidebarTrigger';

// ============================================================================
// SidebarRail
// ============================================================================

/**
 * Default English label for SidebarRail. For production use, pass a translated
 * label via the `label` prop using the `sidenav.toggleSidebar` locale key.
 * @see SidebarRailProps.label for usage example
 */
const SIDEBAR_RAIL_DEFAULT_LABEL = `Toggle Sidebar (${SIDEBAR_KEYBOARD_SHORTCUT.toUpperCase()})`;

const SidebarRail = React.forwardRef<HTMLButtonElement, SidebarRailProps>(
  (
    { className, label = SIDEBAR_RAIL_DEFAULT_LABEL, 'data-testid': testId, ...props },
    ref
  ) => {
    const { toggleSidebar, state } = useSidebar();
    const collapsible = useCollapsibleVariant();

    return (
      <button
        ref={ref}
        data-testid={testId}
        data-sidebar="rail"
        data-slot="sidebar-rail"
        aria-label={label}
        aria-keyshortcuts={SIDEBAR_KEYBOARD_SHORTCUT}
        tabIndex={0}
        onClick={toggleSidebar}
        title={label}
        className={cn(sidebarRailVariants({ collapsible, state }), className)}
        {...props}
      />
    );
  }
);
SidebarRail.displayName = 'SidebarRail';

// ============================================================================
// SidebarInset
// ============================================================================

const SidebarInset = React.forwardRef<HTMLElement, SidebarInsetProps>(
  ({ className, ...props }, ref) => {
    const { variant } = useSidebar();
    return (
      <main
        ref={ref}
        data-slot="sidebar-inset"
        className={cn(sidebarInsetClasses(variant), className)}
        {...props}
      />
    );
  }
);
SidebarInset.displayName = 'SidebarInset';

// ============================================================================
// SidebarInput
// ============================================================================

const SidebarInput = React.forwardRef<HTMLInputElement, SidebarInputProps>(
  ({ className, ...props }, ref) => {
    return (
      <Input
        ref={ref}
        data-slot="sidebar-input"
        data-sidebar="input"
        className={cn(sidebarInputClasses, className)}
        {...props}
      />
    );
  }
);
SidebarInput.displayName = 'SidebarInput';

// ============================================================================
// SidebarHeader
// ============================================================================

const SidebarHeader = React.forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ className, ...props }, ref) => {
    const collapsible = useCollapsibleVariant();

    return (
      <div
        ref={ref}
        data-slot="sidebar-header"
        data-sidebar="header"
        className={cn(sidebarHeaderVariants({ collapsible }), className)}
        {...props}
      />
    );
  }
);
SidebarHeader.displayName = 'SidebarHeader';

// ============================================================================
// SidebarFooter
// ============================================================================

const SidebarFooter = React.forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ className, ...props }, ref) => {
    const collapsible = useCollapsibleVariant();

    return (
      <div
        ref={ref}
        data-slot="sidebar-footer"
        data-sidebar="footer"
        className={cn(sidebarFooterVariants({ collapsible }), className)}
        {...props}
      />
    );
  }
);
SidebarFooter.displayName = 'SidebarFooter';

// ============================================================================
// SidebarAction
// ============================================================================

const SidebarAction = React.forwardRef<HTMLDivElement, SidebarActionProps>(
  ({ className, tooltip, children, ...props }, ref) => {
    const { isMobile, state, collapsible } = useSidebar();
    const collapsibleVariant = useCollapsibleVariant();

    // Hide tooltip when expanded, on mobile, or in label-icons mode
    const hideTooltip =
      state !== 'collapsed' || isMobile || collapsible === 'label-icons';

    const wrappedChildren = tooltip ? (
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side="right"
          align="center"
          hidden={hideTooltip}
          className="px-2 py-1"
        >
          {tooltip}
        </TooltipContent>
      </Tooltip>
    ) : (
      children
    );

    return (
      <div
        ref={ref}
        data-slot="sidebar-action"
        data-sidebar="action"
        className={cn(
          sidebarActionVariants({ collapsible: collapsibleVariant, mobile: isMobile }),
          className
        )}
        {...props}
      >
        {wrappedChildren}
      </div>
    );
  }
);
SidebarAction.displayName = 'SidebarAction';

// ============================================================================
// SidebarSeparator
// ============================================================================

const SidebarSeparator = React.forwardRef<HTMLHRElement, SidebarSeparatorProps>(
  ({ className, ...props }, ref) => {
    return (
      <Separator
        ref={ref}
        data-slot="sidebar-separator"
        data-sidebar="separator"
        className={cn(sidebarSeparatorClasses, className)}
        {...props}
      />
    );
  }
);
SidebarSeparator.displayName = 'SidebarSeparator';

// ============================================================================
// SidebarContent
// ============================================================================

const SidebarContent = React.forwardRef<HTMLDivElement, SidebarContentProps>(
  ({ className, children, ...props }, ref) => {
    const { isMobile, state, collapsible: sidebarCollapsible } = useSidebar();
    const collapsibleVariant = useCollapsibleVariant();

    // Desktop: wrap in NavigationMenu for keyboard navigation with floating panels
    // SidebarPanelProvider enables viewport to track active trigger position
    if (!isMobile) {
      return (
        <SidebarPanelProvider>
          <NavigationMenuVerticalTracked
            className={cn(
              sidebarContentVariants({ collapsible: collapsibleVariant }),
              className
            )}
          >
            <NavigationMenuList asChild>
              <div
                ref={ref}
                data-slot="sidebar-content"
                data-sidebar="content"
                className={cn(
                  'flex min-h-0 flex-1 flex-col gap-2',
                  collapsibleVariant === 'label-icons' && [
                    'gap-0 pt-0.5',
                    // Remove vertical padding from groups, keep first top and last bottom
                    '[&>[data-sidebar=group]]:py-0',
                  ]
                )}
                {...props}
              >
                {children}
              </div>
            </NavigationMenuList>
            <SidebarPanelViewport sidebarState={state} collapsible={sidebarCollapsible} />
          </NavigationMenuVerticalTracked>
        </SidebarPanelProvider>
      );
    }

    // Mobile: no NavigationMenu (uses drawer-based navigation)
    return (
      <div
        ref={ref}
        data-slot="sidebar-content"
        data-sidebar="content"
        className={cn(
          sidebarContentVariants({ collapsible: collapsibleVariant }),
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SidebarContent.displayName = 'SidebarContent';

// ============================================================================
// SidebarGroup
// ============================================================================

const SidebarGroup = React.forwardRef<HTMLDivElement, SidebarGroupProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="sidebar-group"
        data-sidebar="group"
        className={cn(sidebarGroupClasses, className)}
        {...props}
      />
    );
  }
);
SidebarGroup.displayName = 'SidebarGroup';

// ============================================================================
// SidebarGroupLabel
// ============================================================================

const SidebarGroupLabel = React.forwardRef<HTMLDivElement, SidebarGroupLabelProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'div';
    const { isMobile } = useSidebar();
    const collapsible = useCollapsibleVariant();

    return (
      <Comp
        ref={ref}
        data-slot="sidebar-group-label"
        data-sidebar="group-label"
        className={cn(
          sidebarGroupLabelVariants({ collapsible, mobile: isMobile }),
          className
        )}
        {...props}
      />
    );
  }
);
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

// ============================================================================
// SidebarGroupAction
// ============================================================================

const SidebarGroupAction = React.forwardRef<HTMLButtonElement, SidebarGroupActionProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const collapsible = useCollapsibleVariant();

    return (
      <Comp
        ref={ref}
        data-slot="sidebar-group-action"
        data-sidebar="group-action"
        className={cn(sidebarGroupActionVariants({ collapsible }), className)}
        {...props}
      />
    );
  }
);
SidebarGroupAction.displayName = 'SidebarGroupAction';

// ============================================================================
// SidebarGroupContent
// ============================================================================

const SidebarGroupContent = React.forwardRef<HTMLDivElement, SidebarGroupContentProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot="sidebar-group-content"
        data-sidebar="group-content"
        className={cn(sidebarGroupContentClasses, className)}
        {...props}
      />
    );
  }
);
SidebarGroupContent.displayName = 'SidebarGroupContent';

// ============================================================================
// SidebarMenu
// ============================================================================

const SidebarMenu = React.forwardRef<HTMLUListElement, SidebarMenuProps>(
  ({ className, ...props }, ref) => {
    const collapsible = useCollapsibleVariant();

    return (
      <ul
        ref={ref}
        data-slot="sidebar-menu"
        data-sidebar="menu"
        className={cn(sidebarMenuVariants({ collapsible }), className)}
        {...props}
      />
    );
  }
);
SidebarMenu.displayName = 'SidebarMenu';

// ============================================================================
// SidebarMenuItem
// ============================================================================

const SidebarMenuItem = React.forwardRef<HTMLLIElement, SidebarMenuItemProps>(
  ({ className, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'li';

    return (
      <Comp
        ref={ref}
        data-slot="sidebar-menu-item"
        data-sidebar="menu-item"
        className={cn(sidebarMenuItemClasses, className)}
        {...props}
      />
    );
  }
);
SidebarMenuItem.displayName = 'SidebarMenuItem';

// ============================================================================
// SidebarMenuButtonMobile (internal helper for mobile panel navigation)
// ============================================================================

interface SidebarMenuButtonMobileProps extends SidebarMenuButtonProps {
  collapsibleVariant: SidebarCollapsible;
  state: 'expanded' | 'collapsed';
  collapsible: SidebarCollapsible;
}

const SidebarMenuButtonMobile = React.forwardRef<
  HTMLButtonElement,
  SidebarMenuButtonMobileProps
>(
  (
    {
      asChild = false,
      isActive = false,
      variant = 'default',
      size = 'default',
      panelContent,
      panelId,
      panelAction,
      className,
      onClick,
      children,
      collapsibleVariant,
      ...props
    },
    ref
  ) => {
    const {
      registerPanel,
      unregisterPanel,
      setActivePanelContent,
      setActivePanelAction,
      setMobileView,
    } = usePanelContext();
    const intl = useDesignSystemIntl();
    const viewDetailsLabel = intl.formatMessage({
      defaultMessage: 'View details',
      id: 'YzlSgg6RAw',
      description:
        'Accessible label on the mobile sidebar menu chevron that opens the panel view',
    });

    // Register panel content and action on mount, unregister on unmount
    React.useEffect(() => {
      if (panelContent && panelId) {
        registerPanel(panelId, panelContent, panelAction);
        return () => {
          unregisterPanel(panelId);
        };
      }
    }, [panelContent, panelId, panelAction, registerPanel, unregisterPanel]);

    // Handler for chevron button - only opens panel, doesn't trigger navigation
    const handleChevronClick = React.useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation(); // Prevent onClick from firing
        if (panelContent) {
          setActivePanelContent(panelContent);
          setActivePanelAction(panelAction ?? null);
          setMobileView('panel');
        }
      },
      [
        panelContent,
        panelAction,
        setActivePanelContent,
        setActivePanelAction,
        setMobileView,
      ]
    );

    // When panelContent exists with onClick (routing case):
    // - Container wraps both buttons with relative positioning
    // - Main button handles navigation with onClick
    // - Chevron accessory button (absolute positioned) handles panel opening
    if (panelContent && onClick) {
      return (
        <div className="group/menu-item relative">
          <button
            ref={ref}
            type="button"
            data-slot="sidebar-menu-button"
            data-sidebar="menu-button"
            data-size={size}
            data-active={isActive}
            data-has-panel="true"
            className={cn(
              sidebarMenuButtonVariants({
                variant,
                size,
                collapsible: collapsibleVariant,
                mobile: true,
              }),
              // Add right padding for chevron button (size-9 = 36px + 4px spacing = 40px)
              'pe-10',
              className
            )}
            onClick={onClick}
            {...props}
          >
            {children}
          </button>
          {/* Chevron accessory button - opens panel view, positioned absolutely like desktop SidebarMenuAction */}
          <button
            type="button"
            onClick={handleChevronClick}
            className="absolute end-1 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-md p-0 text-text-secondary outline-none ring-border-focus transition-transform after:absolute after:-inset-2 hover:bg-background-secondary-hover focus-visible:ring-2"
            aria-label={viewDetailsLabel}
          >
            <ChevronRightIcon
              className="size-4 shrink-0 rtl:-scale-x-100"
              aria-hidden="true"
            />
          </button>
        </div>
      );
    }

    // When panelContent exists without onClick (no routing):
    // - Whole button is clickable and opens panel
    // - This matches the original behavior for non-routing stories
    if (panelContent && !onClick) {
      const handleClick = () => {
        setActivePanelContent(panelContent);
        setActivePanelAction(panelAction ?? null);
        setMobileView('panel');
      };

      return (
        <button
          ref={ref}
          type="button"
          data-slot="sidebar-menu-button"
          data-sidebar="menu-button"
          data-size={size}
          data-active={isActive}
          data-has-panel="true"
          className={cn(
            sidebarMenuButtonVariants({
              variant,
              size,
              collapsible: collapsibleVariant,
              mobile: true,
            }),
            'justify-between',
            className
          )}
          onClick={handleClick}
          {...props}
        >
          {/*
            Wrapper span for mobile menu button with panelContent.
            Uses descendant selectors to style nested elements (e.g., <a><svg><span></a>):
            - [&>*]: Make child element (e.g., <a>) flex with proper layout
            - [&>*]:gap-1.5: Match the gap from mobile variant (h-10 gap-1.5 px-3)
            - [&_svg]: Style icons inside nested elements to match mobile variant
          */}
          <span className="flex min-w-0 flex-1 items-center [&>*]:flex [&>*]:min-w-0 [&>*]:flex-1 [&>*]:items-center [&>*]:gap-1.5 [&_svg]:size-4.5 [&_svg]:shrink-0">
            {children}
          </span>
          <ChevronRightIcon
            className="size-4 shrink-0 text-text-tertiary"
            aria-hidden="true"
          />
        </button>
      );
    }

    // No panel content - standard button behavior
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        data-slot="sidebar-menu-button"
        data-sidebar="menu-button"
        data-size={size}
        data-active={isActive}
        className={cn(
          sidebarMenuButtonVariants({
            variant,
            size,
            collapsible: collapsibleVariant,
            mobile: true,
          }),
          className
        )}
        onClick={onClick}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
SidebarMenuButtonMobile.displayName = 'SidebarMenuButtonMobile';

// ============================================================================
// SidebarMenuButtonLabelIcons
// ============================================================================

/**
 * Props for SidebarMenuButtonLabelIcons component.
 * Simplified compared to SidebarMenuButton - no tooltip, panel, or size props.
 * Size is full-width with 64px min-height for label-icons mode (or 36px in icon-only mode).
 */
export interface SidebarMenuButtonLabelIconsProps extends React.ComponentProps<'button'> {
  /**
   * Visual variant of the menu button
   * @default 'default'
   */
  variant?: SidebarMenuButtonVariant;

  /**
   * Render as a child element via Slot
   * @default false
   */
  asChild?: boolean;

  /**
   * Whether the menu button is currently active
   * @default false
   */
  isActive?: boolean;

  /**
   * Layout mode for the button.
   * - `default`: Full-width grid with icon row + label row
   * - `iconOnly`: Compact 36px icon-only button (no label, for footer actions)
   * @default 'default'
   */
  layout?: 'default' | 'iconOnly';
}

/**
 * Menu button component for label-icons sidebar mode.
 *
 * Uses a vertical grid layout with icon on top and label below.
 * Designed specifically for the always-collapsed label-icons sidebar mode.
 *
 * Features:
 * - 64px tall (minimum), full-width grid layout
 * - Icon row: 44px with 36px rounded background on hover
 * - Label row: auto height, text wraps and centers
 * - No tooltip in default layout (labels are always visible)
 * - Tooltip support in iconOnly layout (handled by parent SidebarMenuButton)
 * - Supports action variant with primary-colored icon background
 *
 * @example
 * ```tsx
 * <SidebarMenuButtonLabelIcons>
 *   <AudioLines />
 *   <span>Scribe</span>
 * </SidebarMenuButtonLabelIcons>
 * ```
 *
 * @example
 * ```tsx
 * // With asChild for custom element (e.g., Link)
 * <SidebarMenuButtonLabelIcons asChild isActive>
 *   <Link href="/scribe">
 *     <AudioLines />
 *     <span>Scribe</span>
 *   </Link>
 * </SidebarMenuButtonLabelIcons>
 * ```
 */
const SidebarMenuButtonLabelIcons = React.forwardRef<
  HTMLButtonElement,
  SidebarMenuButtonLabelIconsProps
>(
  (
    {
      asChild = false,
      isActive = false,
      layout = 'default',
      variant = 'default',
      className,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const variantStyles = sidebarMenuButtonLabelIconsVariants({
      layout,
      variant,
    });

    return (
      <Comp
        ref={ref}
        data-slot="sidebar-menu-button"
        data-sidebar="menu-button"
        data-active={isActive}
        className={cn(variantStyles, className)}
        onClick={onClick}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);
SidebarMenuButtonLabelIcons.displayName = 'SidebarMenuButtonLabelIcons';

// ============================================================================
// SidebarMenuButton
// ============================================================================

const SidebarMenuButton = React.forwardRef<HTMLButtonElement, SidebarMenuButtonProps>(
  (
    {
      asChild = false,
      isActive = false,
      variant = 'default',
      size = 'default',
      layout = 'default',
      tooltip,
      showTooltipWhenExpanded = false,
      panelContent,
      panelAction,
      panelOnNavigate,
      panelId,
      className,
      onClick,
      children,
      ...props
    },
    ref
  ) => {
    const { isMobile, state, collapsible } = useSidebar();
    const collapsibleVariant = useCollapsibleVariant();

    // Generate a stable panel ID if not provided
    const generatedPanelId = React.useId();
    const effectivePanelId = panelId ?? generatedPanelId;

    // For mobile with panel content, we need to access panel context
    // We use a wrapper component pattern to avoid hooks in callbacks
    // Note: Only enabled when IS_SIDEBAR_PANELS_ENABLED is true
    if (IS_SIDEBAR_PANELS_ENABLED && panelContent && isMobile) {
      return (
        <SidebarMenuButtonMobile
          ref={ref}
          asChild={asChild}
          isActive={isActive}
          variant={variant}
          size={size}
          tooltip={tooltip}
          panelContent={panelContent}
          panelAction={panelAction}
          panelId={effectivePanelId}
          className={className}
          onClick={onClick}
          collapsibleVariant={collapsibleVariant}
          state={state}
          collapsible={collapsible}
          {...props}
        >
          {children}
        </SidebarMenuButtonMobile>
      );
    }

    // Label-icons mode uses dedicated component with vertical grid layout.
    // This avoids CSS specificity battles between flex (standard) and grid (label-icons) layouts.
    // Note: panel props and size are not supported in label-icons mode
    // (panels not applicable, size is full-width min-h-16 or 36px for iconOnly).
    if (collapsibleVariant === 'label-icons' && !isMobile) {
      const button = (
        <SidebarMenuButtonLabelIcons
          ref={ref}
          asChild={asChild}
          isActive={isActive}
          variant={variant}
          layout={layout}
          className={className}
          onClick={onClick}
          {...props}
        >
          {children}
        </SidebarMenuButtonLabelIcons>
      );

      if (tooltip && layout === 'iconOnly') {
        const tooltipProps =
          typeof tooltip === 'string' ? { children: tooltip } : tooltip;
        const { children: tooltipChildren, ...restTooltipProps } = tooltipProps;

        return (
          <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent
              side="right"
              align="center"
              className="px-2 py-1"
              {...restTooltipProps}
            >
              {tooltipChildren}
              <TooltipArrow />
            </TooltipContent>
          </Tooltip>
        );
      }

      return button;
    }

    // Common button props
    const buttonClassName = cn(
      sidebarMenuButtonVariants({
        variant,
        size,
        collapsible: collapsibleVariant,
        mobile: isMobile,
      }),
      className
    );

    const dataProps = {
      'data-slot': 'sidebar-menu-button' as const,
      'data-sidebar': 'menu-button' as const,
      'data-size': size,
      'data-active': isActive,
    };

    // When we have panel content, use PanelFloating without tooltip
    // (floating panel replaces tooltip functionality)
    // Note: Only enabled when IS_SIDEBAR_PANELS_ENABLED is true
    if (IS_SIDEBAR_PANELS_ENABLED && panelContent && !isMobile) {
      const trigger = asChild ? (
        <Slot
          ref={ref}
          className={buttonClassName}
          onClick={onClick}
          {...dataProps}
          {...props}
        >
          {children}
        </Slot>
      ) : (
        <button
          ref={ref}
          className={buttonClassName}
          onClick={onClick}
          {...dataProps}
          {...props}
        >
          {children}
        </button>
      );

      return (
        <PanelFloating
          id={effectivePanelId}
          trigger={trigger}
          content={panelContent}
          action={panelAction}
          onNavigate={panelOnNavigate}
        />
      );
    }

    // Build the element with optional tooltip wrapping (no panel case)
    if (tooltip) {
      const tooltipProps = typeof tooltip === 'string' ? { children: tooltip } : tooltip;

      // Hide tooltip when expanded (unless showTooltipWhenExpanded), on mobile, or in label-icons mode
      const hideTooltip =
        (!showTooltipWhenExpanded && state !== 'collapsed') ||
        isMobile ||
        collapsible === 'label-icons';

      // When showing tooltip in expanded state, position below the button
      const tooltipSide =
        showTooltipWhenExpanded && state === 'expanded' ? 'bottom' : 'right';

      const button = asChild ? (
        <Slot
          ref={ref}
          className={buttonClassName}
          onClick={onClick}
          {...dataProps}
          {...props}
        >
          {children}
        </Slot>
      ) : (
        <button
          ref={ref}
          className={buttonClassName}
          onClick={onClick}
          {...dataProps}
          {...props}
        >
          {children}
        </button>
      );

      return (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent
            side={tooltipSide}
            align="center"
            hidden={hideTooltip}
            className="px-2 py-1"
            {...tooltipProps}
          />
        </Tooltip>
      );
    }

    // Simple case: no wrapping, render button directly
    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={buttonClassName}
          onClick={onClick}
          {...dataProps}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        className={buttonClassName}
        onClick={onClick}
        {...dataProps}
        {...props}
      >
        {children}
      </button>
    );
  }
);
SidebarMenuButton.displayName = 'SidebarMenuButton';

// ============================================================================
// SidebarMenuAction
// ============================================================================

const SidebarMenuAction = React.forwardRef<HTMLButtonElement, SidebarMenuActionProps>(
  ({ className, asChild = false, showOnHover = true, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    const { isMobile } = useSidebar();
    const collapsible = useCollapsibleVariant();

    return (
      <Comp
        ref={ref}
        data-slot="sidebar-menu-action"
        data-sidebar="menu-action"
        className={cn(
          sidebarMenuActionVariants({ collapsible, mobile: isMobile }),
          showOnHover && sidebarMenuActionShowOnHoverClasses,
          className
        )}
        {...props}
      />
    );
  }
);
SidebarMenuAction.displayName = 'SidebarMenuAction';

// ============================================================================
// SidebarMenuBadge
// ============================================================================

const SidebarMenuBadge = React.forwardRef<HTMLDivElement, SidebarMenuBadgeProps>(
  ({ className, ...props }, ref) => {
    const collapsible = useCollapsibleVariant();

    return (
      <div
        ref={ref}
        data-slot="sidebar-menu-badge"
        data-sidebar="menu-badge"
        className={cn(sidebarMenuBadgeVariants({ collapsible }), className)}
        {...props}
      />
    );
  }
);
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

// ============================================================================
// SidebarMenuSkeleton
// ============================================================================

const SidebarMenuSkeleton = React.forwardRef<HTMLDivElement, SidebarMenuSkeletonProps>(
  ({ className, showIcon = false, ...props }, ref) => {
    const width = React.useMemo(() => {
      return `${Math.floor(Math.random() * 40) + 50}%`;
    }, []);

    return (
      <div
        ref={ref}
        data-slot="sidebar-menu-skeleton"
        data-sidebar="menu-skeleton"
        className={cn(sidebarMenuSkeletonClasses, className)}
        {...props}
      >
        {showIcon && (
          <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />
        )}
        <Skeleton
          className="h-4 max-w-[var(--skeleton-width)] flex-1"
          data-sidebar="menu-skeleton-text"
          style={
            {
              '--skeleton-width': width,
            } as React.CSSProperties
          }
        />
      </div>
    );
  }
);
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

// ============================================================================
// SidebarMenuSub
// ============================================================================

const SidebarMenuSub = React.forwardRef<HTMLUListElement, SidebarMenuSubProps>(
  ({ className, ...props }, ref) => {
    const collapsible = useCollapsibleVariant();

    return (
      <ul
        ref={ref}
        data-slot="sidebar-menu-sub"
        data-sidebar="menu-sub"
        className={cn(sidebarMenuSubVariants({ collapsible }), className)}
        {...props}
      />
    );
  }
);
SidebarMenuSub.displayName = 'SidebarMenuSub';

// ============================================================================
// SidebarMenuSubItem
// ============================================================================

const SidebarMenuSubItem = React.forwardRef<HTMLLIElement, SidebarMenuSubItemProps>(
  ({ className, ...props }, ref) => {
    return (
      <li
        ref={ref}
        data-slot="sidebar-menu-sub-item"
        data-sidebar="menu-sub-item"
        className={cn(sidebarMenuSubItemClasses, className)}
        {...props}
      />
    );
  }
);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

// ============================================================================
// SidebarMenuSubButton
// ============================================================================

const SidebarMenuSubButton = React.forwardRef<
  HTMLAnchorElement,
  SidebarMenuSubButtonProps
>(({ asChild = false, size = 'md', isActive = false, className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a';
  const { isMobile } = useSidebar();
  const collapsible = useCollapsibleVariant();

  return (
    <Comp
      ref={ref}
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        sidebarMenuSubButtonVariants({ size, collapsible, mobile: isMobile }),
        className
      )}
      {...props}
    />
  );
});
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

// ============================================================================
// Exports
// ============================================================================

export {
  Sidebar,
  SidebarAction,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuButtonLabelIcons,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
};
